import { type FC } from 'react';
import cn from 'classnames';
import { transform } from 'framer-motion';

import { Picture } from '@/components/ui/Picture/Picture';

import css from './styles.module.scss';

import starsPng from '/img/dice-game/stars-pattern.png';
import starsWebp from '/img/dice-game/stars-pattern.webp';

export type TilesBgVariantType =
  | 'green-active'
  | 'purple-active'
  | 'purple'
  | 'purple-fill'
  | 'start'
  | 'white';

interface TilesBgProps {
  variant: TilesBgVariantType;
  withStarsBg?: boolean;
  className?: string;
}

const TilesBg: FC<TilesBgProps> = ({ variant, withStarsBg, className }) => {
  const calcElipseWidth = transform([0, 56], ['0%', '100%'], { clamp: true });

  const styles: Record<
    TilesBgVariantType,
    {
      topElipse: string;
      bg?: string;
      light?: string;
      bottomElipse?: string;
    }
  > = {
    'green-active': {
      topElipse: 'bg-green',
      bg: 'bg-accent/10',
      light: css.lightDefault,
      bottomElipse: 'bg-gold',
    },
    'purple-active': {
      topElipse: 'bg-irrigo-purple',
      bg: 'bg-irrigo-purple/10',
      light: css.lightDefaultImage,
      bottomElipse: css['bottomElipse--purple'],
    },
    'purple': {
      topElipse: 'bg-irrigo-purple',
      light: css.lightBottom,
      bottomElipse: css['bottomElipse--purple'],
    },
    'purple-fill': {
      topElipse: 'bg-irrigo-purple',
      light: css.lightBottomImage,
      bottomElipse: css['bottomElipse--purple'],
    },
    'white': {
      topElipse: 'bg-[#C5C5C5] !blur-[14px]',
      bottomElipse: css.smallBottomElipseWhite,
    },
    'start': {
      topElipse: 'bg-irrigo-purple',
      light: css.lightBottom,
      bottomElipse: css.bottomElipseGold,
    },
  };

  return (
    <div
      className={cn(
        'absolute top-[2px] left-[2px] w-[calc(100%_-_4px)] aspect-square rounded-md',
        className,
      )}
    >
      <div className="relative w-full h-full rounded-md overflow-hidden">
        <span
          style={{
            width: calcElipseWidth(58.11),
            bottom: calcElipseWidth(40.73),
          }}
          className={cn(css.topElipse, styles[variant].topElipse)}
        ></span>
        <div
          className={cn(
            'w-full h-full relative rounded-md z-[2]',
            styles[variant].bg,
          )}
        ></div>
        {styles[variant].light && (
          <div className={cn(css.greenBg, styles[variant].light)}></div>
        )}
        {withStarsBg && (
          <Picture
            className="w-full h-full absolute top-0 left-0 z-[2]"
            img={starsPng}
            webp={starsWebp}
            alt="stars"
          />
        )}
        {styles[variant].bottomElipse && (
          <span
            className={cn(css.bottomElipse, styles[variant].bottomElipse)}
          ></span>
        )}
      </div>
    </div>
  );
};

export { TilesBg };
